<template>
  <div id="user-list">
    <equipe-add-new
      v-model="isAddNewEquipeSidebarActive"
      @refetch-data="getEquipes"
    />

    <contadores
      :total="contadores.total"
      :ativos="contadores.ativos"
      :inativos="contadores.inativos"
      @alterarFiltro="atualizarFiltro"
    />

    <v-card>
      <v-card-title> Filtrar </v-card-title>
      <v-row class="px-2 ma-0">
        <v-col
          cols="12"
          sm="6"
        >
          <v-autocomplete
            v-model.trim="filtroNome"
            dense
            outlined
            clearable
            hide-no-data
            hide-selected
            label="Nome Gestor"
            placeholder="Digite algo para pesquisar"
            item-text="integranteNome"
            item-value="integranteNome"
            :items="colaboradoresNome"
            :loading="isAutompleteLoading"
            :search-input.sync="searchNome"
            @change="getEquipes"
          />
        </v-col>

        <v-col
          cols="12"
          sm="6"
        >
          <v-autocomplete
            v-model.trim="filtroEmail"
            dense
            outlined
            clearable
            hide-no-data
            hide-selected
            label="Email Gestor"
            placeholder="Digite algo para pesquisar"
            item-text="integranteEmail"
            item-value="integranteId"
            :items="colaboradoresEmail"
            :loading="isAutompleteLoading"
            :search-input.sync="searchEmail"
            @change="getEquipes"
          />
        </v-col>
      </v-row>

      <v-divider />

      <v-card-text class="d-flex align-center flex-wrap pb-0">
        <v-text-field
          v-model.trim="search"
          dense
          outlined
          hide-details
          placeholder="Pesquisar"
          class="user-search me-3 mb-4"
          @keyup="getEquipes"
        />

        <v-spacer />

        <div class="d-flex align-center flex-wrap">
          <v-btn
            v-if="!acessoPost"
            title
            color="success"
            class="mb-4 px-4"
            disabled
          >
            <v-icon
              left
              size="25"
              class="mr-1"
            >
              {{ icons.mdiPlus }}
            </v-icon>
            <span>Criar Equipe</span>
          </v-btn>

          <v-btn
            v-else
            title
            color="success"
            class="mb-4 px-4"
            @click.stop="isAddNewEquipeSidebarActive = !isAddNewEquipeSidebarActive"
          >
            <v-icon
              left
              size="25"
              class="mr-1"
            >
              {{ icons.mdiPlus }}
            </v-icon>
            <span>Criar Equipe</span>
          </v-btn>
        </div>
      </v-card-text>

      <v-col cols="12">
        <div style="min-height: 4px">
          <v-progress-linear
            v-if="loading"
            indeterminate
          />
        </div>

        <table-equipe
          :items="equipes"
          :headers="headers"
          :acesso-disable="acessoDisable"
          @confirmar-inativacao="getEquipes"
        />
      </v-col>
    </v-card>
  </div>
</template>

<script>
import { onMounted, ref, watch } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import { mdiAccount, mdiPlus } from '@mdi/js'
import Contadores from '@/components/contadores/Contadores.vue'
import TableEquipe from '@/components/tables/TableEquipe.vue'
import EquipeAddNew from './EquipeListAddNew.vue'
import EquipeService from '@/services/modules/EquipeService'
import useAuthStore from '@/store/auth'

export default {
  components: {
    TableEquipe,
    Contadores,
    EquipeAddNew,
  },

  setup() {
    const { funcionalidades } = storeToRefs(useAuthStore())

    const isAddNewEquipeSidebarActive = ref(false)
    const equipes = ref([])
    const loading = ref(true)
    const search = ref('')
    const searchNome = ref('')
    const searchEmail = ref('')
    const filtroStatus = ref('')
    const filtroNome = ref('')
    const filtroEmail = ref('')

    const contadores = ref({
      total: 0,
      ativos: 0,
      inativos: 0,
    })

    const idPost = 1086
    const idDisable = 1088
    const acessoPost = ref(true)
    const acessoDisable = ref(true)
    const colaboradoresNome = ref([])
    const colaboradoresEmail = ref([])
    const isAutompleteLoading = ref(undefined)

    const headers = [
      { text: 'NOME', value: 'nome' },
      { text: 'DESCRIÇÃO', value: 'descricao' },
      { text: 'GESTOR', value: 'nomeGestorPrincipal' },
      { text: 'STATUS', value: 'ativo' },
      {
        text: 'OPÇÔES',
        value: 'actions',
        align: 'center',
      },
    ]

    watch(
      searchNome,
      val => {
        if (val) {
        // Items have already been loaded
          if (colaboradoresNome.value.length > 0) return

          // Items have already been requested
          if (isAutompleteLoading.value) return

          isAutompleteLoading.value = true

          EquipeService
            .getIntegrantes(val)
            .then(response => {
              colaboradoresNome.value = response.data.data.filter(element => element.funcaoId === 1)
            })
            .finally(() => {
              isAutompleteLoading.value = false
            })
        } else {
          colaboradoresNome.value = []
        }
      },
    )

    watch(
      searchEmail,
      val => {
        if (val) {
        // Items have already been loaded
          if (colaboradoresEmail.value.length > 0) return

          // Items have already been requested
          if (isAutompleteLoading.value) return

          isAutompleteLoading.value = true

          EquipeService
            .getIntegrantes(null, val)
            .then(response => {
              colaboradoresEmail.value = response.data.data.filter(element => element.funcaoId === 1)
            })
            .finally(() => {
              isAutompleteLoading.value = false
            })
        } else {
          colaboradoresEmail.value = []
        }
      },
    )

    async function getEquipes() {
      try {
        equipes.value = await EquipeService.get()

        contadores.value.total = equipes.value.length
        contadores.value.ativos = equipes.value.filter(i => i.ativo).length
        contadores.value.inativos = equipes.value.filter(i => !i.ativo).length

        if (search.value) {
          equipes.value = equipes.value.filter(
            e => e.nome.toLowerCase().includes(search.value.toLowerCase())
                || e.descricao.toLowerCase().includes(search.value.toLowerCase()),
          )
        }

        if (filtroStatus.value) {
          equipes.value = equipes.value.filter(
            e => e.ativo === filtroStatus.value || e.ativo.toString() === filtroStatus.value,
          )
        }

        if (filtroNome.value) {
          equipes.value = equipes.value.filter(
            e => e.nomeGestorPrincipal === filtroNome.value || e.nomeGestorPrincipal.toString() === filtroNome.value,
          )
        }

        if (filtroEmail.value) {
          equipes.value = equipes.value.filter(
            e => e.idGestorPrincipal === filtroEmail.value || e.idGestorPrincipal.toString() === filtroEmail.value,
          )
        }

        equipes.value.sort((a, b) => new Date(b.dataCadastro) - new Date(a.dataCadastro))

        setTimeout(() => {
          loading.value = false
        }, 1000)
      } finally {
        loading.value = false
      }
    }

    function atualizarFiltro(item) {
      filtroStatus.value = item
      getEquipes()
    }

    function permissoes() {
      if (!funcionalidades.value.includes(idPost)) {
        acessoPost.value = false
      }
      if (!funcionalidades.value.includes(idDisable)) {
        acessoDisable.value = false
      }
    }

    onMounted(() => {
      getEquipes()
      // permissoes()
    })

    return {
      isAddNewEquipeSidebarActive,
      equipes,
      loading,
      search,
      filtroStatus,
      filtroNome,
      filtroEmail,
      acessoPost,
      acessoDisable,
      colaboradoresNome,
      colaboradoresEmail,
      isAutompleteLoading,
      searchEmail,
      searchNome,
      headers,
      contadores,
      getEquipes,
      atualizarFiltro,
      icons: {
        mdiPlus,
        mdiAccount,
      },
    }
  },
}
</script>
